import React, { useEffect, useState } from 'react'
import { useFocusManagement } from '@/utils/hooks/useFocusManagement'
import {
  TextField,
  Box,
  Typography,
  Alert,
  Button,
  Link,
  Divider,
} from '@mui/material'
import Loading from '@/components/Loading'
import ErrorMessage from '@/components/ErrorMessage'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import { getEffectiveOrgUuid } from '@/utils/utils'
import { UUID } from 'crypto'
import LoginSignup from '@/components/LoginSignup'
import { Unauthenticated, Authenticated } from '@/utils/auth/auth'
import { HelpFormData, submitHelpRequest } from '@/api/feedbackAPI'
import { usePageTitle } from '@/utils/hooks/usePageTitle'

// Interface for the form data

export default function HelpFormPage() {
  usePageTitle('Help and Support')

  const { user, token, isLoading: userLoading, orgs } = getCurrentUser()
  const focusRef = useFocusManagement()
  const {
    orgData,
    isLoading: orgsLoading,
    isError,
  } = useCurrentUserOrgs(token as string, user, orgs as UUID[])
  const [organisation, setOrganisation] = useState(orgData?.[0])
  const [contactType, setContactType] = useState('email')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const effectiveOrgUuid = getEffectiveOrgUuid(user, orgs)

  useEffect(() => {
    if (user && orgData && orgData.length > 0) {
      if (!user.selected_org_uuid) {
        setOrganisation(orgData[0])
        return
      } else {
        const selectedOrg = orgData.find(
          (org) => org.uuid === user.selected_org_uuid,
        )
        if (selectedOrg) {
          setOrganisation(selectedOrg || orgData[0])
        }
      }
    }
  }, [orgData, organisation, user])

  // State for form data
  const [formData, setFormData] = useState<HelpFormData>({
    organisationName: organisation?.name || '',
    orgUuid: effectiveOrgUuid,
    userName: user?.first_name + ' ' + user?.last_name,
    email: user?.email,
    phone: '',
    description: '',
  })

  // State for form validation
  const [errors, setErrors] = useState({
    phone: false,
    description: false,
  })

  // Handle text field changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })

    // Clear error for this field if it was previously marked as error
    if (errors[name as keyof typeof errors]) {
      setErrors({
        ...errors,
        [name]: false,
      })
    }
  }

  function handleContactTypeToggle(selectedContactType: string) {
    setContactType(selectedContactType)
  }

  // Handle form submission
  async function handleSubmit() {
    if (!effectiveOrgUuid || !token) {
      return
    }

    // Validate form
    const newErrors = {
      phone:
        (contactType === 'phone' &&
          formData.phone !== undefined &&
          formData.phone.trim() === '') ||
        (contactType === 'phone' &&
          formData.phone !== undefined &&
          !/^\+?\d{9,13}$/.test(formData.phone)),
      description: formData.description.trim() === '',
    }

    setErrors(newErrors)

    if (!Object.values(newErrors).includes(true)) {
      setSubmitError(null)
      setSubmitting(true)
      setSubmitted(false)

      try {
        const result = await submitHelpRequest(
          { ...formData },
          contactType,
          token,
        )

        // Reset form
        setFormData({
          ...formData,
          phone: '',
          description: '',
        })
        setContactType('email')

        if (result) {
          setSubmitted(true)
          setSubmitting(false)
        } else {
          setSubmitting(false)
          setSubmitError(
            'Failed to submit request - please email help@fundsorter.com directly',
          )
        }
      } catch {
        setSubmitting(false)
        setSubmitError(
          'Failed to submit request - please email help@fundsorter.com directly',
        )
      }
    }
  }

  useEffect(() => {
    if (!userLoading && orgs && orgs.length === 0) {
      window.location.href = '/'
    }
  }, [userLoading, orgs])

  return (
    <>
      <Unauthenticated>
        <LoginSignup />
      </Unauthenticated>
      <Authenticated>
        {userLoading || orgsLoading ? (
          <Loading />
        ) : isError ? (
          <ErrorMessage />
        ) : !user || !token || !organisation || !organisation.uuid ? (
          <ErrorMessage />
        ) : (
          <>
            <Box
              sx={{
                margin: '0 auto',
                marginTop: 2,
                width: '100%',
                pb: { xs: 20, md: 5 },
                px: { xs: 2, md: 15 },
              }}
            >
              <Typography
                variant="h1"
                component="h1"
                ref={focusRef}
                tabIndex={-1}
                sx={{
                  mb: 1,
                  textAlign: 'left',
                  fontSize: '34px',
                  '&:focus': {
                    outline: 'none',
                  },
                }}
              >
                Help and Support
              </Typography>
              <Box
                sx={{
                  mb: 2,
                  mt: 6,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body1" component="div">
                  Help is available in three ways:{' '}
                  <ul>
                    <li>
                      Click on the Help button at the bottom right of this
                      screen. Use the chat widget to find instant answers to
                      your questions.
                    </li>
                    <li>
                      Browse the Help Documents here:{' '}
                      <Link
                        href="https://help.fundsorter.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        https://help.fundsorter.com
                      </Link>
                    </li>
                    <li>
                      Fill out the form below or email us at{' '}
                      <Link
                        href="mailto:help@fundsorter.com"
                        sx={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        help@fundsorter.com
                      </Link>{' '}
                      and one of the team will get back to you shortly.
                    </li>
                  </ul>
                </Typography>
              </Box>
              <Divider sx={{ mt: 4, mb: 2 }} />
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Typography
                  variant="h2"
                  component="h2"
                  sx={{
                    mt: 4,
                    mb: 2,
                    textAlign: 'center',
                    fontSize: '28px',
                    '&:focus': {
                      outline: 'none',
                    },
                  }}
                >
                  Help Request Form
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#1C3045',
                    textAlign: 'center',
                    maxWidth: 500,
                    margin: '0 auto',
                  }}
                >
                  We're here to help! Please fill out this form and our support
                  team will get back to you as soon as possible.
                </Typography>
              </Box>

              <Box
                component="form"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  width: '100%',
                  maxWidth: 600,
                  margin: '0 auto',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {/* Category field */}
                {/* <FormControl fullWidth margin="dense">
                    <InputLabel id="category-select-label">
                      Issue Category
                    </InputLabel>
                    <Select
                      labelId="category-select-label"
                      id="category-select"
                      value={formData.category}
                      label="Issue Category"
                      onChange={handleCategoryChange}
                    >
                      <MenuItem value="technical">Technical Support</MenuItem>
                      <MenuItem value="billing">Billing Questions</MenuItem>
                      <MenuItem value="feature">Feature Request</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl> */}

                {/* Description field */}
                <TextField
                  name="description"
                  label="Describe Your Issue"
                  multiline
                  minRows={4}
                  maxRows={10}
                  value={formData.description}
                  onChange={handleInputChange}
                  fullWidth
                  margin="dense"
                  required
                  error={errors.description}
                  helperText={
                    errors.description ? 'Please describe your issue' : ''
                  }
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#757575',
                    },
                  }}
                />

                <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
                  How should we contact you?
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    gap: 2,
                    mb: 1,
                  }}
                >
                  <Button
                    disableRipple
                    sx={{
                      borderColor: '#1C3045',
                      bgcolor: contactType === 'email' ? '#1C3045' : '#fff',
                      color: contactType === 'email' ? '#fff' : '#1C3045',
                      textTransform: 'none',
                      fontSize: '16px',
                      '&:focus': {
                        border: '2px solid #1C3045',
                      },
                    }}
                    variant={contactType === 'email' ? 'contained' : 'outlined'}
                    onClick={() => handleContactTypeToggle('email')}
                    aria-pressed={contactType === 'email'}
                  >
                    Email
                  </Button>
                  <Button
                    disableRipple
                    sx={{
                      borderColor: '#1C3045',
                      bgcolor: contactType === 'phone' ? '#1C3045' : '#fff',
                      color: contactType === 'phone' ? '#fff' : '#1C3045',
                      textTransform: 'none',
                      fontSize: '16px',
                      '&:focus': {
                        border: '2px solid #1C3045',
                      },
                    }}
                    color="primary"
                    variant={contactType === 'phone' ? 'contained' : 'outlined'}
                    onClick={() => handleContactTypeToggle('phone')}
                    aria-pressed={contactType === 'phone'}
                  >
                    Phone
                  </Button>
                </Box>
                {contactType === 'phone' && (
                  <Box maxWidth={300}>
                    <TextField
                      name="phone"
                      label="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      margin="dense"
                      required
                      error={errors.phone}
                      helperText={
                        errors.phone ? (
                          'Please enter a valid phone number - no spaces (international + prefix accepted)'
                        ) : (
                          <>
                            Digits only, no spaces. <br />
                            International + prefix (e.g. +64) accepted.
                          </>
                        )
                      }
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#757575',
                        },
                      }}
                    />
                  </Box>
                )}

                <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
                  Alternatively, for onboarding support please book time with
                  the team{' '}
                  <Link
                    href="https://calendar.app.google/H1vX5FdQBhZZBksj9"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </Link>
                </Typography>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || submitted}
                  variant="contained"
                  disableRipple
                  sx={{
                    borderRadius: 3,
                    bgcolor: '#1C3045',
                    color: 'white',
                    m: 0.5,
                    width: '50%',
                    fontSize: '22px',
                    textTransform: 'none',
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </Button>

                {submitError && <Alert severity="error">{submitError}</Alert>}
                {submitted && !submitError && (
                  <Alert severity="success">
                    Thank you for submitting your request. Our support team will
                    get back to you as soon as possible.
                  </Alert>
                )}
              </Box>
            </Box>
          </>
        )}
      </Authenticated>
    </>
  )
}
